import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";

import styles from "./section-info-modal.module.css";

export type SectionInfoVariant = "filters" | "completionMetrics" | "candidateResults";

const sectionContent: Record<SectionInfoVariant, { title: string; body: React.ReactNode }> = {
  filters: {
    title: "Filters",
    body: (
      <>
        <p>
          The date range filters candidate assessments based on when they were <strong>started and completed</strong>.
        </p>
        <ul>
          <li>
            If <strong>both dates are set</strong>, results include assessments that fall within the selected range.
          </li>
          <li>
            If the <strong>"From" date is blank</strong>, results include all assessments from the beginning of recorded
            data <strong>up to the selected "To" date</strong>.
          </li>
          <li>
            If the <strong>"To" date is blank</strong>, results include all assessments from the selected{" "}
            <strong>"From" date through today</strong>.
          </li>
          <li>
            If <strong>neither date is set</strong>, results will include <strong>all</strong> candidate results.
          </li>
        </ul>
      </>
    ),
  },
  completionMetrics: {
    title: "Completion Metrics",
    body: (
      <>
        <p>These completion metrics apply the selected date range as follows:</p>
        <ul>
          <li>
            <strong>Assessments Started:</strong> Counts all assessments that were{" "}
            <strong>started between the From and To dates</strong>.
          </li>
          <li>
            <strong>Assessments Completed:</strong> Counts all assessments that were{" "}
            <strong>both started and completed between the From and To dates</strong>.
          </li>
          <li>
            <strong>Completion Rate:</strong> The percentage of started assessments that were also completed within the
            selected date range.
          </li>
        </ul>
      </>
    ),
  },
  candidateResults: {
    title: "Candidate Results",
    body: (
      <>
        <p>
          The charts below include candidate results only{" "}
          <strong>
            from candidates who both started and completed their assessment within the selected date range
          </strong>
          .
        </p>
        <p>
          Assessments that were started or completed outside the selected time window are not included in chart
          calculations.
        </p>
      </>
    ),
  },
};

export function SectionInfoModal({ variant, onClose }: { variant: SectionInfoVariant | null; onClose: () => void }) {
  const content = variant ? sectionContent[variant] : null;

  return (
    <BasicModal isModalOpen={!!variant} handleCancel={onClose} autoHeight={true}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>{content?.title}</h2>
        <div className={styles.modalBody}>{content?.body}</div>
      </div>
    </BasicModal>
  );
}
